import { inject } from "@angular/core";
import { Routes } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { defaultCountryProductInfoRedirect } from "./core/matchers/default-country-product-info.redirect";
import { defaultProductInfoMatcher } from "./core/matchers/default-product-info.matcher";
import { defaultProductInfoRedirect } from "./core/matchers/default-product-info.redirect";
import { defaultYearProductInfoRedirect } from "./core/matchers/default-year-product-info.redirect";
import { productDetailRedirect } from "./core/matchers/product-detail-redirect";
import { ProductArgumentDetailDialog } from "./features/product-argument-detail/product-argument-detail-dialog/product-argument-detail-dialog";
import { ProductArgumentsTable } from "./features/product-arguments-tech-data/product-arguments-table/product-arguments-table";
import { ProductArgumentsTechDataComponent } from "./features/product-arguments-tech-data/product-arguments-tech-data";
import { ProductTechDataOverview } from "./features/product-arguments-tech-data/product-tech-data-overview/product-tech-data-overview";
import { ProductDetailComponent } from "./features/product-detail/product-detail";
import { ProductDocumentsComponent } from "./features/product-documents/product-documents";
import { ProductOverviewComponent } from "./features/product-overview/product-overview";
import { ProductPicturesComponent } from "./features/product-pictures/product-pictures";
import { ProductsCompare } from "./features/products-compare/products-compare";
import { ProductsCompareArguments } from "./features/products-compare/products-compare-arguments/products-compare-arguments";
import { ProductsCompareTechData } from "./features/products-compare/products-compare-tech-data/products-compare-tech-data";
import { Contacts } from "./shared/components/contacts/contacts";
import { InternalServerError } from "./shared/components/internal-server-error/internal-server-error";
import { NotFoundComponent } from "./shared/components/not-found/not-found";
import { compareArgumentsResolver } from "./shared/resolvers/compare-arguments.resolver";
import { compareTechDataResolver } from "./shared/resolvers/compare-tech-data.resolver";
import { competitorsResolver } from "./shared/resolvers/competitors.resolver";
import { productArgumentsResolver } from "./shared/resolvers/product-arguments.resolver";
import { productDescendantsResolver } from "./shared/resolvers/product-descendants.resolver";
import { productDetailResolver } from "./shared/resolvers/product-detail.resolver";
import { productDocumentCategoriesResolver } from "./shared/resolvers/product-document-categories-resolver";
import { productDocumentsLanguagesResolver } from "./shared/resolvers/product-documents-languages.resolver";
import { productDocumentsResolver } from "./shared/resolvers/product-documents.resolver";
import {
  productFamiliesPathYearResolver,
  productFamiliesResolver,
  productFamiliesYearsResolver,
} from "./shared/resolvers/product-families.resolver";
import { productPicturesResolver } from "./shared/resolvers/product-pictures.resolver";
import { productSeriesResolver } from "./shared/resolvers/product-series.resolver";
import { productTechDatalResolver } from "./shared/resolvers/product-tech-data.resolver";
import { translateLanguageResolver } from "./shared/resolvers/translate-language.resolver";

export const piaRoutes: Routes = [
  // product-info
  {
    path: "pia/:country/:language/product-info",
    redirectTo: defaultYearProductInfoRedirect,
  },
  {
    path: "pia/:country/product-info",
    redirectTo: defaultCountryProductInfoRedirect,
  },
  // product argument detail route

  {
    path: "pia/:country/:language/:year/product/:id/arguments/:productId/comparison/:competitorId",
    component: ProductsCompare,
    resolve: {
      translateLang: translateLanguageResolver,
      productDetails: productDetailResolver,
      productDescendants: productDescendantsResolver,
    },
    children: [
      {
        path: "",
        component: ProductsCompareArguments,
        resolve: {
          argumentsComparison: compareArgumentsResolver,
          competitors: competitorsResolver,
        },
        children: [
          {
            path: "detail/product/:argumentId",
            pathMatch: "full",
            component: ProductArgumentDetailDialog,
            data: { type: "product", openModal: true },
          },
          {
            path: "detail/competitor/:argumentId",
            pathMatch: "full",
            component: ProductArgumentDetailDialog,
            data: { type: "competitor", openModal: true },
          },
        ],
      },
    ],
  },
  {
    path: "pia/:country/:language/:year/product/:id/tech-data/:productId/comparison/:competitorId",
    component: ProductsCompare,
    resolve: {
      translateLang: translateLanguageResolver,
      productDetails: productDetailResolver,
      productDescendants: productDescendantsResolver,
      productTechData: productTechDatalResolver,
      techDataComparison: compareTechDataResolver,
    },
    children: [
      {
        path: "",
        pathMatch: "full",
        component: ProductsCompareTechData,
        resolve: {
          competitors: competitorsResolver,
        },
      },
    ],
  },
  {
    path: "pia/:country/:language/:year",
    children: [
      {
        path: "contacts",
        pathMatch: "full",
        component: Contacts,
        resolve: {
          translateLang: translateLanguageResolver,
        },
      },
      {
        path: "product-info",
        pathMatch: "full",
        component: ProductOverviewComponent,
        resolve: {
          translateLang: translateLanguageResolver,
          years: productFamiliesYearsResolver,
          selectedYear: productFamiliesPathYearResolver,
          productFamilies: productFamiliesResolver,
          productSeries: productSeriesResolver,
          documentLanguages: productDocumentsLanguagesResolver,
        },
      },
      {
        path: "product/:id",
        component: ProductDetailComponent,
        title: () =>
          inject(TranslateService).get("features.product-overview.title"),
        resolve: {
          translateLang: translateLanguageResolver,
          productDetails: productDetailResolver,
          productDocumentCategories: productDocumentCategoriesResolver,
          productDescendants: productDescendantsResolver,
        },
        children: [
          {
            path: "",
            // Use custom redirect function for product document categories
            redirectTo: productDetailRedirect,
            pathMatch: "full",
          },
          {
            path: "documents/not-found",
            component: NotFoundComponent,
          },
          {
            path: "documents/:docCatId",
            pathMatch: "full",
            component: ProductDocumentsComponent,
            resolve: {
              documents: productDocumentsResolver,
            },
          },
          {
            path: "pictures",
            pathMatch: "full",
            component: ProductPicturesComponent,
            resolve: {
              pictures: productPicturesResolver,
            },
          },
          {
            path: "arguments/:productId",
            component: ProductArgumentsTechDataComponent,
            resolve: {
              productDescendants: productDescendantsResolver,
              arguments: productArgumentsResolver,
              competitors: competitorsResolver,
            },
            children: [
              {
                path: "",
                component: ProductArgumentsTable,
                children: [
                  {
                    path: "detail/:argumentId",
                    pathMatch: "full",
                    component: ProductArgumentDetailDialog,
                    data: { type: "product", openModal: true },
                  },
                ],
              },
            ],
          },
          {
            path: "tech-data/:productId",
            component: ProductArgumentsTechDataComponent,
            resolve: {
              productDescendants: productDescendantsResolver,
              productTechData: productTechDatalResolver,
              competitors: competitorsResolver,
            },
            children: [
              {
                path: "",
                pathMatch: "full",
                component: ProductTechDataOverview,
              },
            ],
          },
        ],
      },
    ],
  },
  // defaulting pia routes
  {
    matcher: defaultProductInfoMatcher,
    redirectTo: defaultProductInfoRedirect,
  },
  // catch all (scoped under /pia — poff owns the global '' and '**' routes)
  {
    path: "pia/error",
    component: InternalServerError,
    resolve: { translateLang: translateLanguageResolver },
  },
  {
    component: NotFoundComponent,
    path: "pia/**",
  },
];
